/**
 * useLandingARKflowLandingReactionClockLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait LandingARKflowLandingReactionClock
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, runTickFrame, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import { isFieldValue } from '@almadar/core';
import type { EntityFieldWrite, EntityFrameState, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, LandingARChemReactionState, LandingARKflowLandingReactionClockEventPayloadMap } from '@app/shared';

export interface LandingARKflowLandingReactionClockConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  autoRun: boolean;
  compact: boolean;
  doneLabel: string;
  matchMode: string;
  ofWord: string;
  pauseLabel: string;
  pausedLabel: string;
  playLabel: string;
  resetLabel: string;
  runWhenVisible: boolean;
  runningLabel: string;
  speedLabel: string;
  stepIntervalMs: number;
  stepLabel: string;
  stepWord: string;
  summary: string;
  title: string;
  totalSteps: number;
}

const DEFAULT_LANDING_AR_KFLOW_LANDING_REACTION_CLOCK_CONFIG = {
  autoRun: false,
  compact: false,
  doneLabel: "Done",
  matchMode: "",
  ofWord: "of",
  pauseLabel: "Pause",
  pausedLabel: "Paused",
  playLabel: "Play",
  resetLabel: "Reset",
  runWhenVisible: false,
  runningLabel: "Running",
  speedLabel: "Speed",
  stepIntervalMs: 550,
  stepLabel: "Step",
  stepWord: "Step",
  summary: "Reactants are rearranged into products as the reaction proceeds.",
  title: "",
  totalSteps: 0,
} as LandingARKflowLandingReactionClockConfig;

type LandingARKflowLandingReactionClockSlot = 'main';
export interface LandingARKflowLandingReactionClockLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<LandingARKflowLandingReactionClockSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingARChemReactionState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingARKflowLandingReactionClockEventPayloadMap]: (payload?: LandingARKflowLandingReactionClockEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingARKflowLandingReactionClockConfig;
}

export function useLandingARKflowLandingReactionClockLogic(config?: Partial<LandingARKflowLandingReactionClockConfig>): LandingARKflowLandingReactionClockLogicReturn {
  const mergedConfig: LandingARKflowLandingReactionClockConfig = { ...DEFAULT_LANDING_AR_KFLOW_LANDING_REACTION_CLOCK_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'LandingARChemReactionState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'speed': 1 });
  const _sharedTickEmitsRef = useRef<Array<() => void>>([]);
  const _sharedWritersRef = useRef<ReadonlyArray<(scratch: EntityFrameState) => readonly EntityFieldWrite[]>>([
    // writer LandingARKflowLandingReactionClock.transportTick
    (scratch: EntityFrameState): readonly EntityFieldWrite[] => {
      const writes: EntityFieldWrite[] = [];
      const frame: Record<string, EventPayloadValue | undefined> = scratch;
      const _emits = _sharedTickEmitsRef.current;
      if (((frame?.active === true) && ((frame?.running === true) && !(frame?.done === true)))) {
        writes.push({ field: 'elapsedMs', value: ((__v) => (isFieldValue(__v) ? __v : undefined))((+(frame?.elapsedMs ?? 0) + 32)) });
        if (((+(frame?.elapsedMs ?? 0) * +(frame?.speed ?? 0)) >= 550)) {
          writes.push({ field: 'elapsedMs', value: ((__v) => (isFieldValue(__v) ? __v : undefined))(0) });
          writes.push({ field: 'steps', value: ((__v) => (isFieldValue(__v) ? __v : undefined))((+(frame?.steps ?? 0) + 1)) });
          _emits.push(((_p) => () => { eventBus.emit('LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReactionClock', { event: 'LEARN_STEP', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReactionClock' } }); eventBus.emit('UI:LandingAR.LandingARKflowLandingPendulum.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingUnitCircle.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingCell.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingDijkstra.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingGalton.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); })({ steps: frame?.steps }));
        }
      }
      return writes;
    },
  ]);

  // Tick: LandingARChemReactionStateSharedTick (every 32ms)
  useEffect(() => {
    const interval = setInterval(() => {
      if (true) {
        runTickFrame(_sharedEntityId, _sharedWritersRef.current, _sharedStore);
        for (const _emit of _sharedTickEmitsRef.current.splice(0)) _emit();
      }
    }, 32);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<LandingARChemReactionState>;
  const data: Record<string, EntityRow[]> = { 'LandingARChemReactionState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<LandingARKflowLandingReactionClockSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingARChemReactionState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReactionClock', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReactionClock' } }); eventBus.emit('UI:LandingAR.LandingARKflowLandingPendulum.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingUnitCircle.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingCell.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingDijkstra.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingGalton.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); })({ running: fields?.running, steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReactionClock', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReactionClock' } }); eventBus.emit('UI:LandingAR.LandingARKflowLandingPendulum.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingUnitCircle.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingCell.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingDijkstra.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingGalton.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); })({ running: fields?.running, steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = false;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on STEP)
        _fields['steps'] = (+(fields?.steps ?? 0) + 1);
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReactionClock', { event: 'LEARN_STEP', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReactionClock' } }); eventBus.emit('UI:LandingAR.LandingARKflowLandingPendulum.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingUnitCircle.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingCell.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingDijkstra.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingGalton.LEARN_STEP', _p, { trait: 'LandingARKflowLandingReactionClock' }); })({ steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TOGGLE_RUN' && ((fields?.active === true))) {
      // idle → idle (on TOGGLE_RUN)
        _fields['running'] = !fields?.running;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReactionClock', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReactionClock' } }); eventBus.emit('UI:LandingAR.LandingARKflowLandingPendulum.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingUnitCircle.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingCell.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingDijkstra.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingGalton.LEARN_UPDATED', _p, { trait: 'LandingARKflowLandingReactionClock' }); })({ running: fields?.running, steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'RESET' && ((fields?.active === true))) {
      // idle → idle (on RESET)
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_RESET', _p, { trait: 'LandingARKflowLandingReactionClock' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReactionClock', { event: 'LEARN_RESET', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReactionClock' } }); eventBus.emit('UI:LandingAR.LandingARKflowLandingPendulum.LEARN_RESET', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingUnitCircle.LEARN_RESET', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingCell.LEARN_RESET', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingDijkstra.LEARN_RESET', _p, { trait: 'LandingARKflowLandingReactionClock' }); eventBus.emit('UI:LandingAR.LandingARKflowLandingGalton.LEARN_RESET', _p, { trait: 'LandingARKflowLandingReactionClock' }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SPEED_SET' && ((fields?.active === true))) {
      // idle → idle (on SPEED_SET)
        _fields['speed'] = payloadObj?.speed;
        fields = { ...(fields ?? {}), 'speed': _fields['speed'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingARKflowLandingReactionClockSlot[]; clear: LandingARKflowLandingReactionClockSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingARKflowLandingReactionClockSlot[], clear: [] as LandingARKflowLandingReactionClockSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as LandingARKflowLandingReactionClockSlot[], clear: ['main'] as LandingARKflowLandingReactionClockSlot[] };
          case 'RESET': return { write: ['main'] as LandingARKflowLandingReactionClockSlot[], clear: [] as LandingARKflowLandingReactionClockSlot[] };
          case 'SPEED_SET': return { write: ['main'] as LandingARKflowLandingReactionClockSlot[], clear: [] as LandingARKflowLandingReactionClockSlot[] };
          case 'STEP': return { write: ['main'] as LandingARKflowLandingReactionClockSlot[], clear: [] as LandingARKflowLandingReactionClockSlot[] };
          case 'TOGGLE_RUN': return { write: ['main'] as LandingARKflowLandingReactionClockSlot[], clear: [] as LandingARKflowLandingReactionClockSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'LandingARChemReactionState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'LandingARKflowLandingReactionClock': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingARKflowLandingReactionClockEventPayloadMap]: (payload?: LandingARKflowLandingReactionClockEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: LandingARKflowLandingReactionClockEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
    'RESET': () => dispatch('RESET'),
    'SPEED_SET': (payload?: LandingARKflowLandingReactionClockEventPayloadMap['SPEED_SET']) => dispatch('SPEED_SET', payload),
    'LEARN_STEP': (payload?: LandingARKflowLandingReactionClockEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: LandingARKflowLandingReactionClockEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
  };

  useUIEvents(enqueueEvent, 'LandingAR.LandingARKflowLandingReactionClock', ['INIT', 'MODE_SELECTED', 'STEP', 'TOGGLE_RUN', 'RESET', 'SPEED_SET'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOGGLE_RUN' (from LandingARKflowLandingReaction) → trigger 'TOGGLE_RUN'
    const unsub_toggle_run_from_landingarkflowlandingreaction_to_toggle_run = eventBus.on('UI:LandingAR.LandingARKflowLandingReaction.TOGGLE_RUN', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingAR.LandingARKflowLandingReactionClock', 'TOGGLE_RUN')) return;
      enqueueEvent('TOGGLE_RUN', event.payload);
    });
    // Listen for 'STEP' (from LandingARKflowLandingReaction) → trigger 'STEP'
    const unsub_step_from_landingarkflowlandingreaction_to_step = eventBus.on('UI:LandingAR.LandingARKflowLandingReaction.STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingAR.LandingARKflowLandingReactionClock', 'STEP')) return;
      enqueueEvent('STEP', event.payload);
    });
    // Listen for 'RESET' (from LandingARKflowLandingReaction) → trigger 'RESET'
    const unsub_reset_from_landingarkflowlandingreaction_to_reset = eventBus.on('UI:LandingAR.LandingARKflowLandingReaction.RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingAR.LandingARKflowLandingReactionClock', 'RESET')) return;
      enqueueEvent('RESET', event.payload);
    });
    return () => {
      if (typeof unsub_toggle_run_from_landingarkflowlandingreaction_to_toggle_run === 'function') unsub_toggle_run_from_landingarkflowlandingreaction_to_toggle_run();
      if (typeof unsub_step_from_landingarkflowlandingreaction_to_step === 'function') unsub_step_from_landingarkflowlandingreaction_to_step();
      if (typeof unsub_reset_from_landingarkflowlandingreaction_to_reset === 'function') unsub_reset_from_landingarkflowlandingreaction_to_reset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
